import { Component, EventEmitter, Injector, Input, OnInit, Output } from '@angular/core';
import {
    AbstractControl,
    FormBuilder,
    FormControl,
    FormGroup, NgControl, NG_VALIDATORS,
    NG_VALUE_ACCESSOR, ValidationErrors,
    Validators
} from '@angular/forms';

import { FormControlComponent } from '../../../../abstracts/form-control.abstract';
import { BasicInputTypeEnum } from '../../../../enums/basic-input-type.enum';
import { EntityTypeEnum } from '../../../../enums/entity-type.enum';

@Component({
    selector: 'app-address',
    templateUrl: './address.component.html',
    styleUrls: ['./address.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: AddressComponent,
            multi: true,
        },
        {
            provide: NG_VALIDATORS,
            multi: true,
            useExisting: AddressComponent,
        },
    ],
})
export class AddressComponent extends FormControlComponent implements OnInit {
    @Input() public readOnly = false;
    @Input() public entityType: EntityTypeEnum;

    @Output() public elementSizeModification = new EventEmitter<void>();

    public addressForm: FormGroup;

    public basicInputTypeEnum = BasicInputTypeEnum;

    private areValidatorsSet = false;

    private ngControl: NgControl;

    constructor(private builder: FormBuilder, private inj: Injector) {
        super();
    }

    ngOnInit(): void {
        this.ngControl = this.inj.get(NgControl);
        this.ngControl.control.markAllAsTouched = () => {
            this.addressForm.markAllAsTouched();
        };
        this.addressForm = this.builder.group({
            id: [],
            street: [''],
            home_number: ['', Validators.pattern("^[0-9A-Za-z]*$")],
            box: ['', [Validators.maxLength(8), Validators.pattern("^[0-9A-Za-z]*$")]],
            additional_information: ['', Validators.maxLength(255)],
            postal_code: [''],
            city: [''],
            country: ['Belgique'],
        });
        if (!this.readOnly && this.hasRequiredField(this.ngControl.control)) {
            this.setFullValidators();
            this.areValidatorsSet = true;
        }
    }

    public writeValue(value: any): void {
        if (value) {
            this.value = value;
            this.addressForm.patchValue(value, { emitEvent: false });
        }
    }

    public onInput(): void {
        if (this.disabled) {
            return;
        }

        this.value = this.addressForm.value;

        this.onChange(this.value);
        if (!this.touched) {
            this.onTouched();
        }
    }

    public validate(): ValidationErrors | null {
        return this.addressForm.invalid ? { addressInvalid: true } : null;
    }

    public get street(): FormControl {
        return this.addressForm.get('street') as FormControl;
    }

    public get homeNumber(): FormControl {
        return this.addressForm.get('home_number') as FormControl;
    }

    public get box(): FormControl {
        return this.addressForm.get('box') as FormControl;
    }

    public get additionalInformation(): FormControl {
        return this.addressForm.get('additional_information') as FormControl;
    }

    public get postalCode(): FormControl {
        return this.addressForm.get('postal_code') as FormControl;
    }

    public get city(): FormControl {
        return this.addressForm.get('city') as FormControl;
    }

    public get country(): FormControl {
        return this.addressForm.get('country') as FormControl;
    }

    public hasRequiredField = (abstractControl: AbstractControl): boolean => {
        if (abstractControl.validator) {
            const validator: ValidationErrors = abstractControl.validator({} as AbstractControl);
            if (validator && validator.required) {
                return true;
            }
        }
        return false;
    };

    private setFullValidators(): void {
        this.street.setValidators([Validators.required, Validators.maxLength(255)]);
        this.homeNumber.setValidators([Validators.required, Validators.maxLength(8)]);
        this.postalCode.setValidators([Validators.required, Validators.maxLength(8)]);
        this.city.setValidators([Validators.required, Validators.maxLength(255)]);
        this.country.setValidators([Validators.required, Validators.maxLength(255)]);

        this.updateValueAndValidity();
    }

    private updateValueAndValidity(): void {
        this.street.updateValueAndValidity({ emitEvent: false });
        this.homeNumber.updateValueAndValidity({ emitEvent: false });
        this.postalCode.updateValueAndValidity({ emitEvent: false });
        this.city.updateValueAndValidity({ emitEvent: false });
        this.country.updateValueAndValidity({ emitEvent: false });
    }
}
